/**
 * Utilidades para manejo de fechas
 * Convierte entre formato dd/mm/yyyy (display) y yyyy-mm-dd (input type="date")
 *
 * IMPORTANTE: Todos los timestamps en la BD están en UTC. Las funciones de
 * formateo abajo (formatBsAs*) los convierten a la timezone de Buenos Aires
 * (America/Argentina/Buenos_Aires, UTC-3) usando Intl.DateTimeFormat.
 */

const TZ_BSAS = 'America/Argentina/Buenos_Aires';
const LOCALE = 'es-AR';

/**
 * Crea un objeto Date a partir de un string ISO (con o sin "Z").
 * Devuelve null si es inválido.
 */
const _toDate = (iso) => {
  if (!iso) return null;
  if (iso instanceof Date) return isNaN(iso.getTime()) ? null : iso;
  // Si el string ISO no tiene zona horaria (ej "2026-03-05T03:42:52"), asumimos UTC.
  let s = String(iso);
  if (!/[zZ]|[+-]\d{2}:?\d{2}$/.test(s) && /T/.test(s)) {
    s = s + 'Z';
  }
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
};

/**
 * Formatea un timestamp ISO/UTC como fecha + hora en horario de Buenos Aires.
 * Ej: "05/03/2026, 00:42" (para "2026-03-05T03:42Z")
 *
 * @param {string|Date} iso - Timestamp ISO (típicamente UTC desde el backend)
 * @param {object} [opts] - opciones de Intl.DateTimeFormat para customizar
 * @returns {string} Texto formateado (ej "05/03/2026, 14:30") o "" si es inválido
 */
export const formatBsAs = (iso, opts = {}) => {
  const d = _toDate(iso);
  if (!d) return '';
  return new Intl.DateTimeFormat(LOCALE, {
    timeZone: TZ_BSAS,
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
    hour12: false,
    ...opts,
  }).format(d);
};

/**
 * Solo fecha en horario de BsAs. Ej: "05/03/2026"
 */
export const formatBsAsFecha = (iso) => {
  const d = _toDate(iso);
  if (!d) return '';
  return new Intl.DateTimeFormat(LOCALE, {
    timeZone: TZ_BSAS,
    day: '2-digit', month: '2-digit', year: 'numeric',
  }).format(d);
};

/**
 * Solo hora en BsAs. Ej: "14:30"
 */
export const formatBsAsHora = (iso) => {
  const d = _toDate(iso);
  if (!d) return '';
  return new Intl.DateTimeFormat(LOCALE, {
    timeZone: TZ_BSAS,
    hour: '2-digit', minute: '2-digit',
    hour12: false,
  }).format(d);
};

/**
 * Formato largo con día de semana en BsAs.
 * Ej: "jueves, 05 de marzo de 2026, 14:30"
 */
export const formatBsAsLargo = (iso) => {
  const d = _toDate(iso);
  if (!d) return '';
  return new Intl.DateTimeFormat(LOCALE, {
    timeZone: TZ_BSAS,
    weekday: 'long',
    day: '2-digit', month: 'long', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
    hour12: false,
  }).format(d);
};

/**
 * Tiempo relativo human-friendly ("hace 5 min", "hace 2h", "ayer 14:30", "05/03 14:30")
 * Útil para feeds/chats. Usa BsAs como timezone de referencia para el display absoluto.
 */
export const formatBsAsRelativo = (iso) => {
  const d = _toDate(iso);
  if (!d) return '';
  const now = new Date();
  const diffMs = now - d;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'hace instantes';
  if (diffMin < 60) return `hace ${diffMin} min`;
  const diffHs = Math.floor(diffMin / 60);
  if (diffHs < 24) return `hace ${diffHs} h`;
  const diffDias = Math.floor(diffHs / 24);
  if (diffDias === 1) return `ayer ${formatBsAsHora(iso)}`;
  if (diffDias < 7) return `hace ${diffDias} días`;
  // > 7 días → fecha + hora corta
  return new Intl.DateTimeFormat(LOCALE, {
    timeZone: TZ_BSAS,
    day: '2-digit', month: '2-digit',
    hour: '2-digit', minute: '2-digit',
    hour12: false,
  }).format(d);
};

/**
 * Convierte fecha de dd/mm/yyyy a yyyy-mm-dd (para input type="date")
 * @param {string} fecha - Fecha en formato dd/mm/yyyy
 * @returns {string} Fecha en formato yyyy-mm-dd o cadena vacía si es inválida
 */
export const fechaToInput = (fecha) => {
  if (!fecha) return '';
  
  // Si ya está en formato yyyy-mm-dd, devolverla
  if (/^\d{4}-\d{2}-\d{2}$/.test(fecha)) {
    return fecha;
  }
  
  // Convertir de dd/mm/yyyy a yyyy-mm-dd
  const match = fecha.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
  if (match) {
    const [, dia, mes, anio] = match;
    return `${anio}-${mes.padStart(2, '0')}-${dia.padStart(2, '0')}`;
  }
  
  return fecha;
};

/**
 * Convierte fecha de yyyy-mm-dd a dd/mm/yyyy (para mostrar y guardar)
 * @param {string} fecha - Fecha en formato yyyy-mm-dd
 * @returns {string} Fecha en formato dd/mm/yyyy o cadena vacía si es inválida
 */
export const fechaToDisplay = (fecha) => {
  if (!fecha) return '';
  
  // Si ya está en formato dd/mm/yyyy, devolverla
  if (/^\d{1,2}[\/\-]\d{1,2}[\/\-]\d{4}$/.test(fecha)) {
    return fecha.replace(/-/g, '/');
  }
  
  // Convertir de yyyy-mm-dd a dd/mm/yyyy
  const match = fecha.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) {
    const [, anio, mes, dia] = match;
    return `${dia}/${mes}/${anio}`;
  }
  
  return fecha;
};
